import React, {
  memo,
  useState,
  useRef,
  useEffect,
  useMemo,
  useImperativeHandle,
} from 'react';
import { Form } from 'antd';
import { Button, PopAlert } from 'appkit-react';
import _ from 'lodash';

import { valuerDriverEditColumn } from './config/columns.config';
import { FORM_KEYS } from './config/form.keys';
import { INPUT_MAP } from './config/form.config';
import { VALUEDRIVER_FORM_CATE, VALUEDRIVER_OPTIONS } from '@model';
// import FormSwitch from './formSwitch';
import FormExpand from './formExpand';
import TopForm from './topForm';
import { DragableTableFrom as FormList } from '@components';
import * as tipTexts from '@modules/insights/valueDriverManager/tips';

// import '@style/modules/insights/valueDriverManager.scss';
import '../../../../styles/modules/insights/valueDriverManager.scss';

const WRAPPER_KEY = FORM_KEYS.TABLE_WRAPPER.key;

const CATE_KEYS = [
  FORM_KEYS.TYPE.key,
  FORM_KEYS.SYNERGY.key,
  FORM_KEYS.CATEGORY.key,
];

const DEN_KEY = FORM_KEYS.DEN.key;
const BUYER_EBIDTA_KEY = FORM_KEYS.CLIENT_EBITDA.key;
const TARGEt_EBIDTA_KEY = FORM_KEYS.TARGET_EBITDA.key;

const formDefaultValue = {
  [DEN_KEY]: 0,
  [BUYER_EBIDTA_KEY]: null,
  [TARGEt_EBIDTA_KEY]: null,
};
const formLayout = {
  labelCol: { span: 24 },
  wrapperCol: { span: 24 },
};

// const switchOptions = [
//   {
//     key: VALUEDRIVER_FORM_CATE.DOLLAR,
//     label: 'Dollar Value',
//     tip: VALUEDRIVER_TIPS[VALUEDRIVER_FORM_CATE.DOLLAR],
//   },
//   {
//     key: VALUEDRIVER_FORM_CATE.SCALE,
//     label: 'Scale',
//     tip: VALUEDRIVER_TIPS[VALUEDRIVER_FORM_CATE.SCALE],
//   },
// ];

// const COLUMNS = {
//   [VALUEDRIVER_FORM_CATE.SCALE]: columnsOfScale,
//   [VALUEDRIVER_FORM_CATE.DOLLAR]: columnsOfDollar,
// };

const getSelectionLabel = (key, value) => {
  const options = VALUEDRIVER_OPTIONS[key];

  if (!Array.isArray(options)) {
    return value;
  }

  return options.find((opt) => opt.value === value)?.label || value;
};

const formatFormData = (data = {}, format = 1) => {
  try {
    const dataTemp = { ...data };

    Object.keys(dataTemp).forEach((key) => {
      if (CATE_KEYS.includes(key)) {
        dataTemp[key] = getSelectionLabel(key, dataTemp[key]);
      }

      // if (key.toLowerCase().includes(fixedValueKey)) {
      //   dataTemp[key] = null;
      // }
      if (
        [FORM_KEYS.FINANCIAL_IMPACT.key, FORM_KEYS.ONE_TIME_COST.key].includes(
          key,
        )
      ) {
        const optionLabels = VALUEDRIVER_OPTIONS[key].map((i) => i.label);
        const valueWithoutComma =
          typeof dataTemp[key] === 'string'
            ? dataTemp[key].replace(/,/g, '')
            : dataTemp[key];
        if (dataTemp[key] === null) {
          dataTemp[`${key}OfScale`] = null;
          dataTemp[`${key}OfDollarValue`] = null;
        } else if (
          optionLabels.includes(dataTemp[key]) ||
          ['none', 'TBD'].includes(dataTemp[key])
        ) {
          dataTemp[`${key}OfScale`] =
            dataTemp[key] === 'TBD' ? null : dataTemp[key];
          dataTemp[`${key}OfDollarValue`] = null;
        } else if (!Number.isNaN(parseInt(valueWithoutComma))) {
          dataTemp[`${key}OfScale`] = null;
          dataTemp[`${key}OfDollarValue`] =
            parseInt(valueWithoutComma) * format;
        }
        Reflect.deleteProperty(dataTemp, key);
      }

      if (Array.isArray(dataTemp[key])) {
        dataTemp[key] = dataTemp[key].map((data) =>
          formatFormData(data, format),
        );
      }
    });
    return dataTemp;
  } catch (error) {
    return data;
  }
};

const needEnhanced = (col = {}) =>
  [FORM_KEYS.FINANCIAL_IMPACT.key, FORM_KEYS.ONE_TIME_COST.key].includes(
    col.key || col.dataIndex,
  );

const enhanceColumn = (columns, magnification) =>
  [...columns].map((col) => {
    if (needEnhanced(col)) {
      col.title = React.cloneElement(col.title, {
        tip: tipTexts.financialImpactTip(
          getSelectionLabel(FORM_KEYS.FORMAT.key, magnification),
        ),
      });
    }

    return col;
  });

const getFlag = (value) => {
  if (typeof value === 'boolean') {
    return value;
  }

  return null;
};

const LinkURLFormConfig = {
  dataIndex: FORM_KEYS.LinkURL.key,
  key: FORM_KEYS.LinkURL.key,
  style: { width: '42%' },
  formConfig: { ...INPUT_MAP[FORM_KEYS.LinkURL.key] },
  className: 'extra-form',
};

function ManagerForm({
  cRef,
  saveData,
  data,
  isSystemSetting = false,
  afterSaveData,
  goBack: goBackInProps,
  formExpand,
  setFormExpand,
  formChange = () => {},
}) {
  const [form] = Form.useForm();

  const formatSaveData = (_formData) => {
    const formData = formatFormData(
      {
        ..._formData,
      },
      _formData.format,
    );

    if (Array.isArray(formData.valueDriverItems)) {
      formData.valueDriverItems.forEach((i) => {
        if (typeof i.description === 'string') {
          try {
            i.description = JSON.parse(i.description);
          } catch (e) {
            console.log(e);
          }
        }
      });
    }

    const extraData = {
      buyerEbitdaOfCraft: data.buyerEbitdaOfCraft || null,
      targetEbitdaOfCraft: data.targetEbitdaOfCraft || null,
      fiscalYear: data.fiscalYear || null,
      buyerEbitdaSyncFlag: getFlag(data.buyerEbitdaSyncFlag),
      targetEbitdaSyncFlag: getFlag(data.targetEbitdaSyncFlag),
      mode: VALUEDRIVER_FORM_CATE.SCALE,
    };

    return { ...formData, ...extraData };
  };

  useImperativeHandle(cRef, () => ({
    getForm: () => formatSaveData(form.getFieldsValue()),
    validateFields: () => form.validateFields(),
  }));

  const [magnification, toggleMagnification] = useState(1000);
  // const [currentForm, toggleForm] = useState(VALUEDRIVER_FORM_CATE.DOLLAR);
  const [actionLoading, toggleActionLoading] = useState(false);
  const [update, trigUpdate] = useState(false);

  const columns = useMemo(
    () => enhanceColumn(valuerDriverEditColumn, magnification),
    [magnification],
  );

  const fieldMethods = useRef({});
  const registerFieldsMethods = (methods) => {
    fieldMethods.current = methods;
  };

  // const handleSwitchForm = (e = {}) => {
  //   e.target?.value && toggleForm(e.target.value);
  // };

  const resetTitle = () => {
    const titleElem = document.getElementById('project-form-title');
    titleElem.style.display = 'flex';
  };

  // const goBack = () => {
  //   history.push(`/insights/value-driver`);
  // };

  const handleSave = async (e) => {
    // return console.log(e);
    toggleActionLoading(true);

    // const fixedValueKey =
    //   currentForm === VALUEDRIVER_FORM_CATE.SCALE
    //     ? VALUEDRIVER_FORM_CATE.DOLLAR
    //     : VALUEDRIVER_FORM_CATE.SCALE;
    const dataToSave = formatSaveData(e);

    if (typeof saveData !== 'function') {
      return;
    }

    const afterSave = afterSaveData || goBackInProps;

    const result = await saveData(dataToSave);

    toggleActionLoading(false);
    if (result?.success) {
      PopAlert({
        className: 'success-message-popover',
        oneLineDisplay: true,
        duration: 2000,
        status: 'success',
        message: 'SAVED SUCCESSFULLY',
      });
      afterSave();
    }
  };

  const handleFieldsFail = (e) => {
    console.error('error', e);
  };

  const handleSubmitForm = () => {
    form.validateFields().then(handleSave).catch(handleFieldsFail);
  };

  const handleSortEnd = ({ newIndex, oldIndex }) => {
    const { move } = fieldMethods.current;

    if (move) {
      move(oldIndex, newIndex);
      formChange();
    }
  };

  useEffect(() => {
    if (Object.keys(data || {}).length > 0) {
      // data.mode && toggleForm(data.mode.toLowerCase());
      data.format && toggleMagnification(data.format);

      form.setFieldsValue({ ...formDefaultValue, ...data });
    } else {
      form.setFieldsValue(formDefaultValue);
    }
  }, [data]);

  const handleValueChange = (e) => {
    e[FORM_KEYS.TABLE_WRAPPER.key] &&
      _.compact(e[FORM_KEYS.TABLE_WRAPPER.key])[0][FORM_KEYS.HAS_LINK.key] !==
        undefined &&
      trigUpdate((state) => !state);
  };

  return (
    <Form
      form={form}
      className="integration-manage-form"
      requiredMark={false}
      {...formLayout}
      onValuesChange={handleValueChange}
    >
      <TopForm
        formInstance={form}
        labelSuf={getSelectionLabel(FORM_KEYS.FORMAT.key, magnification)}
        magnification={magnification}
        toggleMagnification={toggleMagnification}
        data={data}
        isSystemSetting={isSystemSetting}
        // currentForm={currentForm}
        visible={!formExpand}
        formChange={formChange}
      />
      {/*<FormSwitch*/}
      {/*  options={switchOptions}*/}
      {/*  onChange={handleSwitchForm}*/}
      {/*  value={currentForm}*/}
      {/*/>*/}
      <FormExpand
        isSystemSetting={isSystemSetting}
        formExpand={formExpand}
        setFormExpand={setFormExpand}
      />
      <div
        className={`dragable-table-form value-driver-form-table${
          formExpand ? ' expanded' : ''
        }`}
      >
        <FormList
          update={update}
          registerFieldsMethods={registerFieldsMethods}
          onSortEnd={handleSortEnd}
          name={WRAPPER_KEY}
          formInstance={form}
          isActive
          isLoading={actionLoading}
          columns={columns}
          addBtnText="Add New Value Driver"
          configs={{ magnification }}
          maxRow={10}
          shouldHasExtraForm={{
            key: FORM_KEYS.HAS_LINK.key,
            formConfig: LinkURLFormConfig,
            has: (namepath) => Boolean(form.getFieldValue(namepath)),
          }}
          formChange={formChange}
          wrapperContainerClass=".value-driver-form-table"
        />
      </div>
      {isSystemSetting ? null : (
        <div className="action-wrapper">
          <Button
            size="xl"
            kind="secondary"
            onClick={() => {
              if (isSystemSetting) {
                resetTitle();
              }
              goBackInProps();
            }}
            isLoading={actionLoading}
          >
            Cancel
          </Button>
          <Button
            size="xl"
            kind="primary"
            onClick={() => {
              if (isSystemSetting) {
                resetTitle();
              }
              handleSubmitForm();
            }}
            isLoading={actionLoading}
          >
            Save
          </Button>
        </div>
      )}
    </Form>
  );
}

export default memo(ManagerForm);
